/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo } from 'react';
import { Button, Divider, Tooltip, message } from 'antd';
import { InputFromUser } from '../Input/input';
import PropTypes from 'prop-types';
import UploadImage from '../Upload/Upload';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useDispatch, useSelector } from 'react-redux';
import { DOCS_URL } from '../../utils/globals';
import { ActionUploadImage } from '../../store/Actions/CommonAction';
import { ActionGetMedicineList } from '../../store/Actions/PatientAction';
import './expendableTable.css';

const ExpendableTableComponent = ({
  touched = {},
  errors = {},
  onChange = () => { },
  onBlur = () => { },
  emptyDataForCreatingField = {},
  Data = [],
  Columns = [],
  HeaderComponent = null,
  UploadImageFlag = '',
  Title = '',
  maxRows = null,
  minRows = 1,
  showRowNumbers = false,
  onRowAdd = null,
  onRowRemove = null,
  validateBeforeAdd = null,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { medicineList = [], loader = false } = useSelector(state => state?.patient || {});

  /** 🔍 Handle Medicine Search with debouncing */
  const onSearch = useCallback(
    search => {
      const trimmed = search?.trim?.() || '';
      if (trimmed.length >= 2) {
        dispatch(ActionGetMedicineList({ Query: `?name=${trimmed}` }));
      } else if (trimmed.length === 0) {
        dispatch(ActionGetMedicineList({ Query: `` }));
      }
    },
    [dispatch]
  );

  /** 📤 Handle Upload Image */
  const handleUploadChange = useCallback(
    (info, index, value) => {
      if (!info?.file) return;
      const updatedList = [...Data];

      if (info.file.status === 'uploading') {
        const formData = new FormData();
        if (UploadImageFlag) {
          formData.append(UploadImageFlag, 'true');
        }
        formData.append('attachment', info.file.originFileObj);

        dispatch(
          ActionUploadImage({ obj: formData }, res => {
            const fileName = res?.[0];
            if (!fileName || res?.statusCode === 400) {
              message.error('Upload failed. Please try again.');
              return;
            }

            const fileUrl = `${DOCS_URL}${fileName}`;
            updatedList[index] = {
              ...updatedList[index],
              [value.name]: fileName,
            };
            onChange(updatedList);

            info.file = {
              ...info.file,
              uid: `${index}-${value?.name}-${fileName}`,
              name: fileName,
              status: 'done',
              url: fileUrl,
              thumbUrl: fileUrl,
            };
          })
        );
      } else if (info.file.status === 'removed') {
        updatedList[index] = {
          ...updatedList[index],
          [value.name]: '',
        };
        onChange(updatedList);
      }
    },
    [dispatch, UploadImageFlag, Data, onChange]
  );



  /** 🧭 Handle Blur (touched tracking) */
  const handleBlur = useCallback(
    (index, value) => {
      const updatedTouched = { ...touched };
      updatedTouched[index] = {
        ...updatedTouched[index],
        [value?.name || '']: true,
      };
      onBlur(updatedTouched);
    },
    [touched, onBlur]
  );

  /** ➕ Add Row with validation */
  const handleAddRow = useCallback(() => {
    // Check max rows limit
    if (maxRows !== null && Data.length >= maxRows) {
      message.warning(`Maximum ${maxRows} rows allowed`);
      return;
    }

    // Custom validation before adding
    if (validateBeforeAdd) {
      const isValid = validateBeforeAdd(Data);
      if (!isValid) {
        message.error('Please complete the current row before adding a new one');
        return;
      }
    }

    const newRow = { ...emptyDataForCreatingField };
    onChange([...Data, newRow]);

    // Callback after row add
    if (onRowAdd) {
      onRowAdd(Data.length, newRow);
    }
  }, [Data, emptyDataForCreatingField, onChange, maxRows, validateBeforeAdd, onRowAdd]);

  /** ➖ Remove Row with validation */
  const handleRemoveRow = useCallback(
    index => {
      if (index < 0) return;

      // Check minimum rows
      if (Data.length <= minRows) {
        message.warning(`At least ${minRows} row(s) required`);
        return;
      }

      const removedRow = Data[index];
      const updatedList = Data.filter((_, i) => i !== index);
      onChange(updatedList);

      // Callback after row remove
      if (onRowRemove) {
        onRowRemove(index, removedRow);
      }
    },
    [Data, onChange, minRows, onRowRemove]
  );

  /** 🔧 Helper: Safe Array */
  const safeArray = arr => (Array.isArray(arr) ? arr : []);

  /**  Memoized empty state */
  const showEmptyState = useMemo(() => Data.length === 0, [Data.length]);

  /** 🔢 Row count info */
  const rowCountInfo = useMemo(() => {
    let info = `${Data.length} row${Data.length !== 1 ? 's' : ''}`;
    if (maxRows !== null) {
      info += ` / ${maxRows} max`;
    }
    return info;
  }, [Data.length, maxRows]);

  return (
    <div className="expendable-table-wrapper">
      <div className="expendable-table-header">
        <Divider orientation="left" style={{ fontWeight: 600, margin: '16px 0' }}>
          {Title}
          {rowCountInfo && (
            <span className="row-count-badge" style={{ marginLeft: '12px', fontWeight: 400, fontSize: '12px' }}>
              {rowCountInfo}
            </span>
          )}
        </Divider>
        {HeaderComponent}
      </div>

      <div className="invoice-add-table form-group">
        <div className="table-responsive">
          <table className="table table-center add-table-items expendable-table">
            <thead>
              <tr>
                {showRowNumbers && <th className="row-number-header">#</th>}
                {safeArray(Columns).map((col, index) => (
                  <th key={`th-${index}`}>
                    {col?.label}
                    {col?.required && <span className="required-indicator">*</span>}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {showEmptyState ? (
                <tr>
                  <td colSpan={Columns.length + (showRowNumbers ? 1 : 0)} className="text-center py-4">
                    <div className="empty-state">
                      <FeatherIcon icon="inbox" size={32} className="mb-2" />
                      <p className="mb-0">No data available. Click + to add a row.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                safeArray(Data).map((item, index) => (
                  <tr key={`row-${index}`}>
                    {showRowNumbers && (
                      <td className="row-number-cell">
                        <span className="row-number">{index + 1}</span>
                      </td>
                    )}
                    {safeArray(Columns).map((value, fieldIndex) => {
                      const ObjOrValue = [
                        'text',
                        'color',
                        'email',
                        'number',
                        'password',
                        'search',
                        'url',
                        'tel',
                      ].includes(value?.type);

                      const fieldName = value?.name || `col-${fieldIndex}`;
                      const fieldError = errors?.[index]?.[fieldName];
                      const fieldTouched =
                        typeof touched === 'object' ? touched?.[index]?.[fieldName] : false;

                      // --- upload field
                      if (value?.type === 'upload') {
                        return (
                          <td className="col" key={`${fieldName}-${index}-${fieldIndex}`}>
                            <UploadImage
                              fileList={
                                item?.[fieldName]
                                  ? [
                                    {
                                      uid: `${index}-${fieldName}-${item[fieldName]}`,
                                      name: item[fieldName],
                                      status: 'done',
                                      url: `${DOCS_URL}${item[fieldName]}`,
                                      thumbUrl: `${DOCS_URL}${item[fieldName]}`,
                                    },
                                  ]
                                  : []
                              }
                              handleChange={info => handleUploadChange(info, index, value)}
                              errorMessage={fieldError}
                              UploadImagelimit={1}
                            />
                          </td>
                        );
                      }

                      // --- action field
                      if (value?.type === 'action') {
                        const canAdd = !disabled && (maxRows === null || Data.length < maxRows);
                        const canRemove = !disabled && Data.length > minRows;

                        return (
                          <td key={`${fieldName}-action-${index}`}>
                            <div className="action-buttons">
                              {Data.length === index + 1 && (
                                <Tooltip title={canAdd ? 'Add row' : `Maximum ${maxRows} rows`}>
                                  <Button
                                    type="text"
                                    className="action-btn add-btn"
                                    onClick={handleAddRow}
                                    disabled={!canAdd}
                                    icon={<FeatherIcon icon="plus-circle" size={18} />}
                                  />
                                </Tooltip>
                              )}
                              {(index !== 0 || Data.length > 1) && (
                                <Tooltip title={canRemove ? 'Remove row' : `Minimum ${minRows} row(s) required`}>
                                  <Button
                                    type="text"
                                    className="action-btn remove-btn"
                                    onClick={() => handleRemoveRow(index)}
                                    disabled={!canRemove}
                                    icon={<FeatherIcon icon="trash-2" size={18} />}
                                  />
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        );
                      }

                      // --- medicine picker
                      if (value?.type === 'medicinePicker') {
                        return (
                          <td key={`${fieldName}-med-${index}`}>
                            <InputFromUser
                              type="singleSelect"
                              label=""
                              Showlabel={false}
                              name={fieldName}
                              value={item?.[fieldName] || ''}
                              onChange={v => {
                                const updatedList = [...Data];
                                updatedList[index] = {
                                  ...updatedList[index],
                                  [fieldName]: v,
                                };
                                onChange(updatedList);
                              }}
                              onSearch={onSearch}
                              onBlur={() => handleBlur(index, value)}
                              error={fieldError}
                              disabled={disabled || value?.idDisable}
                              touched={fieldTouched}
                              onSearchLoading={loader}
                              options={safeArray(medicineList).map(med => med?.name)}
                              placeholder="Search medicine..."
                            />
                          </td>
                        );
                      }

                      // --- custom UI
                      if (value?.type === 'CustomUi' && value?.CustomUi) {
                        return <td key={`${fieldName}-custom-${index}`}>{value.CustomUi}</td>;
                      }

                      // --- generic input
                      return (
                        <td key={`${fieldName}-input-${index}`}>
                          <InputFromUser
                            type={value?.type || 'text'}
                            label=""
                            Showlabel={false}
                            name={fieldName}
                            touched={fieldTouched}
                            error={fieldError}
                            value={item?.[fieldName] || ''}
                            onChange={v => {
                              const updatedList = [...Data];
                              updatedList[index] = {
                                ...updatedList[index],
                                [fieldName]: ObjOrValue ? (v?.target?.value ?? '') : v,
                              };
                              onChange(updatedList);
                            }}
                            disabled={disabled || value?.idDisable}
                            onBlur={() => handleBlur(index, value)}
                            options={safeArray(value?.options)}
                            placeholder={value?.placeholder || `Enter ${value?.label || ''}`}
                          />
                        </td>
                      );
                    })}
                  </tr>))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

ExpendableTableComponent.propTypes = {
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  Title: PropTypes.string,
  UploadImageFlag: PropTypes.string,
  touched: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  emptyDataForCreatingField: PropTypes.object,
  Columns: PropTypes.array,
  Data: PropTypes.array,
  HeaderComponent: PropTypes.element,
  maxRows: PropTypes.number,
  minRows: PropTypes.number,
  showRowNumbers: PropTypes.bool,
  onRowAdd: PropTypes.func,
  onRowRemove: PropTypes.func,
  validateBeforeAdd: PropTypes.func,
};

export const ExpendableTable = React.memo(ExpendableTableComponent);
